import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ContentType } from '@vivport/backend/types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { ItemSelector } from '@/components/widget/item-selector';
import { useContentCreate, useContentList } from '@/hooks/useContentQueries';
import { useCreateTemplate } from '@/hooks/useTemplateQueries';
import { toContentHref } from '@/utils/content-ref';
import { Notice } from '@/components/ui/notice';

type SourceMode = 'none' | 'existing' | 'upload';

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'pending_approval' }
  | { kind: 'failed'; error: string };

interface CreateTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Prefill an existing FILE as the source (DetailsFile's 'Use as template'). */
  prefilledSourceFileId?: number;
  /** Shown when the prefilled file is not a Word document: extraction needs docx. */
  prefilledSourceIsDocx?: boolean;
}

export function CreateTemplateDialog({
  open,
  onOpenChange,
  prefilledSourceFileId,
  prefilledSourceIsDocx,
}: CreateTemplateDialogProps) {
  const navigate = useNavigate();
  const prefilled = prefilledSourceFileId !== undefined;
  const [name, setName] = useState('');
  const [whenToUse, setWhenToUse] = useState('');
  const [purpose, setPurpose] = useState('');
  const [sourceMode, setSourceMode] = useState<SourceMode>(prefilled ? 'existing' : 'none');
  const [sourceFileId, setSourceFileId] = useState<number | null>(prefilledSourceFileId ?? null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [submitState, setSubmitState] = useState<SubmitState>({ kind: 'idle' });
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const filesQuery = useContentList({ contentTypes: [ContentType.FILE], limit: 100 });
  const createTemplate = useCreateTemplate();
  const createContent = useContentCreate();

  const resetForm = () => {
    setName('');
    setWhenToUse('');
    setPurpose('');
    setSourceMode(prefilled ? 'existing' : 'none');
    setSourceFileId(prefilledSourceFileId ?? null);
    setUploadFile(null);
    setSubmitState({ kind: 'idle' });
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) resetForm();
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    if (submitState.kind === 'submitting') return;
    if (name.trim().length < 3) {
      setSubmitState({ kind: 'failed', error: 'Enter a template name (at least 3 characters).' });
      return;
    }
    if (whenToUse.trim().length < 20) {
      setSubmitState({
        kind: 'failed',
        error: 'Describe when this template is used (at least 20 characters).',
      });
      return;
    }
    setSubmitState({ kind: 'submitting' });
    try {
      let resolvedSourceFileId: number | undefined;
      if (sourceMode === 'existing' && sourceFileId !== null) {
        resolvedSourceFileId = sourceFileId;
      } else if (sourceMode === 'upload') {
        if (uploadFile === null) {
          setSubmitState({ kind: 'failed', error: 'Choose a file to upload.' });
          return;
        }
        const uploaded = await createContent.mutateAsync({
          contentType: ContentType.FILE,
          file: uploadFile,
          title: uploadFile.name,
        });
        resolvedSourceFileId = uploaded.id;
      }

      const response = await createTemplate.mutateAsync({
        name: name.trim(),
        whenToUse: whenToUse.trim(),
        ...(purpose.trim().length >= 20 ? { purpose: purpose.trim() } : {}),
        ...(resolvedSourceFileId !== undefined ? { sourceFileId: resolvedSourceFileId } : {}),
        publishWhenComplete: false,
      });
      if (response.status === 'PENDING_APPROVAL') {
        setSubmitState({ kind: 'pending_approval' });
        return;
      }
      if (response.status !== 'EXECUTED' || !response.output) {
        setSubmitState({ kind: 'failed', error: response.error ?? 'Creating the template failed.' });
        return;
      }
      handleOpenChange(false);
      navigate(toContentHref(response.output.templateCode));
    } catch (error) {
      setSubmitState({
        kind: 'failed',
        error: error instanceof Error ? error.message : 'Creating the template failed.',
      });
    }
  };

  const toSelectorItem = (item: { id: number; title?: string | null }) => ({
    id: item.id,
    title: typeof item.title === 'string' ? item.title : null,
  });
  const fileItems = (filesQuery.data?.items ?? []).map(toSelectorItem);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New template</DialogTitle>
          <DialogDescription>
            Created through the same governed tool Neo uses. With a Word source, the platform
            extracts the layout and builds the draft's sections itself.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-3">
            {prefilled && prefilledSourceIsDocx === false ? (
              <Notice tone="warning" className="p-2">
                Layout extraction needs a Word document (.docx). This template will start freeform,
                with the file attached to it for reference.
              </Notice>
            ) : null}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="create-template-name">Name</Label>
              <Input
                id="create-template-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Site Inspection Report"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="create-template-when">When to use</Label>
              <Textarea
                id="create-template-when"
                rows={2}
                value={whenToUse}
                onChange={(event) => setWhenToUse(event.target.value)}
                placeholder="The circumstances this format is produced in, in your own words"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="create-template-purpose">Purpose (optional)</Label>
              <Textarea
                id="create-template-purpose"
                rows={2}
                value={purpose}
                onChange={(event) => setPurpose(event.target.value)}
              />
            </div>
            {!prefilled ? (
              <div className="flex flex-col gap-1.5">
                <Label>Source document</Label>
                <Tabs value={sourceMode} onValueChange={(value) => setSourceMode(value as SourceMode)}>
                  <TabsList>
                    <TabsTrigger value="none">None (freeform)</TabsTrigger>
                    <TabsTrigger value="existing">Existing file</TabsTrigger>
                    <TabsTrigger value="upload">Upload now</TabsTrigger>
                  </TabsList>
                </Tabs>
                {sourceMode === 'existing' ? (
                  <ItemSelector
                    entityName="file"
                    displayName="File"
                    items={fileItems}
                    selectedId={sourceFileId}
                    onSelect={setSourceFileId}
                    loading={filesQuery.isLoading}
                  />
                ) : null}
                {sourceMode === 'upload' ? (
                  <div className="flex items-center gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      className="hidden"
                      onChange={(event) => setUploadFile(event.target.files?.[0] ?? null)}
                    />
                    <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                      Choose file…
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      {uploadFile?.name ?? 'No file chosen'}
                    </span>
                  </div>
                ) : null}
              </div>
            ) : null}
            {submitState.kind === 'failed' ? (
              <p className="text-sm text-destructive">{submitState.error}</p>
            ) : null}
            {submitState.kind === 'pending_approval' ? (
              <p className="text-sm text-muted-foreground">
                Awaiting approval in Mission Control. The template will be created once an admin
                approves it.
              </p>
            ) : null}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              void handleSubmit();
            }}
            disabled={submitState.kind === 'submitting' || submitState.kind === 'pending_approval'}
          >
            {submitState.kind === 'submitting' ? 'Creating…' : 'Create template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
